import { Query } from "@apollo/client/react/components";
import React from "react";
import * as palette from "@govlaunch/palette";
import { Margin } from "~/components/spacings";
import BadgeCheckIcon from "~/components/icons/BadgeCheckIcon";
import { Loader } from "@govlaunch/core";
import SelfieQuery from "~/lib/queries/SelfieQuery";
import GroupRequestAction from "~/components/actions/GroupRequestAction";
import VendorActionsQuery from "~/lib/queries/VendorActionsQuery";
import {
  IVendorActionsQuery,
  IVendorActionsQueryVariables,
} from "~/lib/queries/__generated__/VendorActionsQuery.generated";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import UpgradeAccountBanner from "~/components/vendor/profile/UpgradeAccountBanner";
import { FormattedMessage } from "react-intl";
import { IPlanTier } from "~/types/types";

interface IVendorActions {
  vendor: NonNullable<IVendorQuery["company"]>;
}

function VendorActions({ vendor }: IVendorActions) {
  // Since fetchPolicy="network-only" doesn't work on the server, we'll only query when rendering in the browser
  if (typeof window === "undefined") {
    return <Loader centered={true} />;
  }

  if (vendor.plan.tier === IPlanTier.Basic) {
    return <UpgradeAccountBanner />;
  }

  return (
    <div
      data-intercom-target="Vendor Actions"
      css={{
        display: "block",
      }}
    >
      <Query<IVendorActionsQuery, IVendorActionsQueryVariables>
        query={VendorActionsQuery}
        fetchPolicy="network-only"
        variables={{
          vendorSlug: vendor.slug,
        }}
      >
        {({ data, loading }) => {
          if (loading || !data || !data.company) {
            return <Loader centered={true} />;
          }

          const { actions } = data.company;

          if (actions.length === 0) {
            return (
              // We refetch the selfie in case admin has taken action on behalf of the user
              <Query query={SelfieQuery} fetchPolicy="network-only">
                {() => {
                  return (
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 5,
                        border: `solid 1px ${palette.lightestGray}`,
                        backgroundColor: palette.white,
                        color: palette.mediumGray,
                        padding: 26,
                      }}
                    >
                      <BadgeCheckIcon />
                      <Margin ml={14}>
                        <span
                          css={{
                            fontSize: 18,
                            fontWeight: 500,
                          }}
                        >
                          <FormattedMessage defaultMessage="No actions to take" id="pWGG1V" />
                        </span>
                      </Margin>
                    </div>
                  );
                }}
              </Query>
            );
          }

          return (
            <div
              css={{
                display: "grid",
                gridAutoFlow: "row",
                gridRowGap: 1,
                border: `solid 1px ${palette.lightestGray}`,
                backgroundColor: palette.lightestGray,
                borderRadius: 5,
                overflow: "hidden",
              }}
            >
              {actions.map((action) => {
                return <GroupRequestAction key={action._id} action={action} entityType="vendor" />;
              })}
            </div>
          );
        }}
      </Query>
    </div>
  );
}

export default VendorActions;
